import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QuoteRenderer from '../templates/QuoteRenderer';
import QuoteOptionTabs from '../Quotes/QuoteOptionTabs';
import './ModernQuoteView.css';
import './QuoteComparisonView.css';

// Create axios instance for public API calls (no auth required)
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

/**
 * QuoteComparisonView
 *
 * Public, read-only page shown to clients when an agent sends a
 * "Send Comparison (All Options)" email. Fetches every sibling option in a
 * multi-option AI-generated quote group (keyed off the group leader's
 * quote_number) and renders the same tabbed comparison experience agents
 * see in the Quote Builder, but without any editing controls.
 *
 * Each tab shows a read-only preview of that package. Clients can then
 * click through to the full single-quote page for whichever option they
 * want, where the existing selection/payment flow already works.
 */
const QuoteComparisonView = () => {
  const { quoteNumber } = useParams();
  const navigate = useNavigate();

  const [comparison, setComparison] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchComparison = async () => {
      try {
        setLoading(true);
        const response = await publicAxios.get(`/public/quotes/comparison/${quoteNumber}`);
        setComparison(response.data);
        setActiveIndex(0);
      } catch (err) {
        console.error('Error fetching quote comparison:', err);
        setError(err.response?.data?.message || 'This comparison link is no longer available.');
      } finally {
        setLoading(false);
      }
    };

    if (quoteNumber) {
      fetchComparison();
    }
  }, [quoteNumber]);

  if (loading) {
    return (
      <div className="modern-quote-container">
        <div className="comparison-loading">Loading your travel package options...</div>
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <div className="modern-quote-container">
        <div className="comparison-error">
          <h2>Unable to Load Options</h2>
          <p>{error || 'This comparison link is no longer available.'}</p>
        </div>
      </div>
    );
  }

  const activeOption = comparison.options[activeIndex] || comparison.options[0];

  const tabOptions = comparison.options.map(option => ({
    id: option.id,
    optionName: option.optionName,
    totalPrice: option.totalAmount
  }));

  const quoteDataForRenderer = {
    quoteTitle: comparison.groupTitle,
    quoteNumber: activeOption.quoteNumber,
    client: comparison.client,
    travelDates: {
      start: activeOption.travelStartDate,
      end: activeOption.travelEndDate
    },
    travelers: {
      adults: activeOption.adults || 1,
      children: Array(activeOption.children || 0).fill({ age: 0 })
    },
    components: activeOption.components || [],
    totalPrice: activeOption.totalAmount,
    depositAmount: activeOption.depositAmount,
    depositDueDate: activeOption.depositDueDate,
    agent_name: comparison.agent?.name || '',
    agent_email: comparison.agent?.email || '',
    agent_phone: comparison.agent?.phone || '',
    agent_business_name: comparison.agent?.businessName || '',
    agent_logo_url: comparison.agent?.logoUrl || ''
  };

  return (
    <div className="modern-quote-container">
      {/* Header */}
      <div className="modern-quote-header">
        <h1>✈️ Compare Your Package Options</h1>
        <div className="quote-number">{comparison.groupTitle}</div>
      </div>

      <div className="modern-quote-content">
        {/* Agent Information */}
        {comparison.agent?.name && (
          <div className="agent-info">
            <div className="agent-avatar">
              {comparison.agent.logoUrl ? (
                <img
                  src={comparison.agent.logoUrl}
                  alt={`${comparison.agent.businessName || comparison.agent.name} Logo`}
                  className="agent-logo-avatar"
                />
              ) : (
                <span>{comparison.agent.name.charAt(0)}</span>
              )}
            </div>
            <div>
              <p><strong>{comparison.agent.businessName || comparison.agent.name}</strong></p>
              <p>Prepared by {comparison.agent.name}</p>
            </div>
          </div>
        )}

        {/* Comparison Tabs (read-only navigation between options) */}
        <div className="comparison-tabs-wrapper">
          <QuoteOptionTabs
            options={tabOptions}
            activeIndex={activeIndex}
            onSelect={setActiveIndex}
          />
        </div>

        {/* Read-only preview of the active option */}
        <div className="comparison-renderer-wrapper">
          <QuoteRenderer
            quoteData={quoteDataForRenderer}
            templateId={activeOption.templateId}
            isAgentPreview={true}
          />
        </div>

        {/* CTA to proceed with full selection/payment flow for this option */}
        <div className="comparison-cta-wrapper">
          <p className="comparison-cta-helper">
            Ready to move forward with <strong>{activeOption.optionName || 'this option'}</strong>?
          </p>
          <button
            type="button"
            className="comparison-cta-button"
            onClick={() => navigate(`/quote/${activeOption.quoteNumber}`)}
          >
            View Full Details &amp; Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteComparisonView;
